// Handing a document the app made to the person: it lives only on SharePoint
// and opens from there in a new tab, or in Edge in the Home Screen app (below)
// — SharePoint is the source of truth, never a copy on the device (DD,
// 2026-10-19). The proposal, the Schedule of Work and the photos (the job's
// Photo - Before folder) all open this way; a failed save says why and is made
// again.
//
// A document takes longer to make than a tap lasts, and a tab opened once it's
// ready is blocked as a pop-up, so the tab is opened on the tap and sent to the
// file when it's saved.
//
// In the Home Screen app ("Higgins Apps" web clip) a new tab would take over the
// full-screen window with no Done or back (#615), so no tab is opened there: the
// document opens in Edge, like every SharePoint link (`OutboundLink openIn="edge"`,
// `@higgins/utils` outbound-href; imported by path so node tests can load it).
import { isIosHomeScreenApp, outboundHref } from '@higgins/utils/outbound-href';

/** Where a saved document went, for the line the screen shows. */
export type OpenedWhere = 'opened in a new tab' | 'opened in Edge';

export function openedWhere(homeScreen: boolean = isIosHomeScreenApp()): OpenedWhere {
  return homeScreen ? 'opened in Edge' : 'opened in a new tab';
}

export interface WaitingTab {
  where: OpenedWhere;
  /** Sends it to the saved document. */
  show(url: string): void;
  /** The document wasn't saved. */
  close(): void;
}

/**
 * The tab a saved document will open in, opened now, inside the tap. It says
 * what it's waiting for; `show(url)` sends it on, `close()` closes it if the
 * document isn't saved. In the Home Screen app no tab is opened (it would trap
 * the person): `show` hands the document to Edge. Null when the browser refused
 * a new tab (the screen's "Open in SharePoint" link is then the way in).
 */
export function openWaitingTab(waitingFor: string, win: Window = window): WaitingTab | null {
  if (isIosHomeScreenApp(win.navigator)) {
    return {
      where: openedWhere(true),
      show: (url) => { win.location.href = outboundHref(url, 'edge', true); },
      close: () => {},
    };
  }
  const tab = win.open('', '_blank');
  if (!tab) return null;
  tab.document.title = waitingFor;
  tab.document.body.style.font = '16px Roboto, system-ui, sans-serif';
  tab.document.body.style.padding = '2rem';
  tab.document.body.textContent = `${waitingFor} It opens here from the job's SharePoint folder once it's saved.`;
  return {
    where: openedWhere(false),
    show: (url) => { tab.location.href = url; },
    close: () => tab.close(),
  };
}
